import PropTypes from 'prop-types';
import { IconClinicalTrials } from '@medifind/icons';
import { Skeleton } from '@medifind/shared-basic-components';
import { CardActionButton } from './card-components';
import Card from './card-components/Card';
import CardIcon from './card-components/CardIcon';
import styles from './ClinicalTrialCardV3.module.scss';

const ClinicalTrialCardV3 = ({
  trial,
  fieldsToHide = [],
  personId,
  orgId,
  projectId,
  codingSlug,
  codingType,
  showDistance,
  priority,
  hideIcon = false,
  category,
  smallAt,
  showSummary = true,
  classes = {},
}) => {
  const {
    status,
    phase,
    interventionType,
    clinicalTrialId,
    title,
    drugList,
    abstract,
    distance,
    patientPopulation,
    publicationDate,
    cleanedAbstract,
  } = trial || {};

  const createHref = () => {
    if (!clinicalTrialId) return null;
    if (process.env.NX_APP === 'MediFind') {
      if (projectId) {
        return `/${codingType === 'procedure' ? 'procedures' : 'conditions'}/${codingSlug}/${projectId}/clinical-trial/${clinicalTrialId}`;
      }
      return `/articles/clinical-trial/${clinicalTrialId}`;
    } else {
      if (personId) {
        return `/people/${personId}/clinical-trial/${clinicalTrialId}`;
      } else if (orgId) {
        return `/organizations/${orgId}/${category}/clinical-trial/${clinicalTrialId}`;
      } else {
        return `/articles/clinical-trial/${clinicalTrialId}`;
      }
    }
  };

  const href = createHref();

  return (
    <Card
      id={`card_clinical-trial_${clinicalTrialId}`} // Id is used by GA, don't change
      priority={priority}
      hideIcon={hideIcon}
      icon={
        <CardIcon
          className={classes.icon}
          color={'#4062bb'}
          icon={<IconClinicalTrials width={80} height={80} style={{ borderRadius: '50%' }} padding={false} showBoxShadow={false} />}
        />
      }
      title={title}
      link={{ to: href }}
      rel="nofollow"
      hasData={!!trial}
      smallAt={smallAt}
      metadata={{
        keyValue: [
          !fieldsToHide.includes('patientPopulation') && { label: 'Who is this study for:', value: patientPopulation },
          { label: 'Enrollment Status:', value: status },
          { label: 'Publish Date:', value: publicationDate },
          { label: 'Intervention Type:', value: interventionType },
          drugList && { label: `Study Drug${drugList.length > 1 ? 's' : ''}:`, value: drugList.length > 0 ? drugList.join(', ') : null },
          { label: 'Study Phase:', value: phase },
          showDistance && { label: 'Distance:', value: distance },
        ],
        last: (
          <div>
            {showSummary && (cleanedAbstract || !trial) ? (
              trial ? (
                <div className={styles['summary']}>
                  {cleanedAbstract.length > 0 ? (
                    <>
                      <p>
                        <b>{cleanedAbstract[0]?.title?.display ? cleanedAbstract[0].title.display : 'Summary: '}</b>{' '}
                        {cleanedAbstract[0].text.length > 400 ? (
                          <span dangerouslySetInnerHTML={{ __html: cleanedAbstract[0].text.slice(0, 400) + '...' }}></span>
                        ) : (
                          <span dangerouslySetInnerHTML={{ __html: cleanedAbstract[0].text }}></span>
                        )}
                      </p>
                    </>
                  ) : (
                    <Skeleton count={4} />
                  )}
                </div>
              ) : (
                <Skeleton count={4} />
              )
            ) : null}
          </div>
        ),
      }}
      buttons={<CardActionButton disabled={!clinicalTrialId} label="VIEW TRIAL" to={href} rel="nofollow" />}
    ></Card>
  );
};

ClinicalTrialCardV3.propTypes = {
  trial: PropTypes.shape({
    clinicalTrialId: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
    conditionId: PropTypes.number,
    title: PropTypes.string,
    status: PropTypes.string,
    interventionType: PropTypes.string,
    publicationDate: PropTypes.string,
    completionDate: PropTypes.string,
    numParticipants: PropTypes.number,
    drugList: PropTypes.array,
  }),
  orgId: PropTypes.number,
  fieldsToHide: PropTypes.array,
  hideIcon: PropTypes.bool,
  showSummary: PropTypes.bool,
  showDistance: PropTypes.bool,
  priority: PropTypes.number,
  category: PropTypes.string,
  smallAt: PropTypes.array,
  classes: PropTypes.object,
};

export { ClinicalTrialCardV3 as default, ClinicalTrialCardV3 };
